import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import { resolveWorkspace, PBDQ_WORKSPACE } from '../../../config/workspaceNav/solutions';
import { isClientWorkspace } from '../../../utils/capabilities';
import AssignmentOutlinedIcon from '@material-ui/icons/AssignmentOutlined';
import BarChartOutlinedIcon from '@material-ui/icons/BarChartOutlined';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';

/**
 * Solutions > COS Children Assessment > Overview — Common Section screen,
 * shared by the Partner, School and Teacher workspaces, same family as
 * SolutionsOverviewPage / SolutionReadinessOverviewPage in this folder.
 *
 * Per explicit instruction, solution overview/preview pages don't need to
 * match a reference mockup's exact layout — they should read as this app's
 * own design instead. So this reuses the SAME building blocks as
 * SolutionReadinessOverviewPage (orange title, plain/accented card styles,
 * pill buttons) rather than the mockup's own "at a glance" stat-strip look,
 * while keeping its content (what it measures / at a glance / what you get).
 */

const BORDER = 'var(--brand-border, #E5E7EB)';
const MUTED = 'var(--brand-text-muted, #6B7280)';
const TEXT = 'var(--brand-text, #111827)';
const ORANGE = '#FF6900';

const WHAT_IT_MEASURES = [
    {
        title: 'Online Safety Readiness',
        desc: 'How well students understand and apply the knowledge, skills, attitudes, and behaviors needed to navigate digital life safely and responsibly.',
    },
    {
        title: 'Online Risk Experiences',
        desc: "Students' reported experiences of cyberbullying, harmful content, unwanted contact, AI-related risks, and other online risks.",
    },
    {
        title: "Support for Children's Digital Lives",
        desc: 'The access, guidance, conversations, and support students experience at home and at school.',
    },
];

const AT_A_GLANCE = [
    { label: 'Ages', value: '8–18' },
    { label: 'Questions', value: '179', caption: 'across 7 sections' },
    { label: 'Typical completion', value: 'About 45 minutes', caption: 'for a 13-year-old' },
    { label: 'Deployment', value: 'One class to multiple schools', caption: 'or networks' },
];

const WHAT_YOU_GET = [
    'Track assessment deployment and participation across your schools.',
    'Compare readiness, risks, and support across schools and countries.',
    'Identify areas and groups requiring priority attention.',
    'Use results to guide learning programs and system interventions.',
];

// Same pill buttons as SolutionReadinessOverviewPage.
const primaryButtonSx = {
    backgroundColor: ORANGE,
    color: '#fff',
    textTransform: 'none',
    fontWeight: 700,
    fontSize: '16px',
    borderRadius: '50px',
    px: 3,
    py: 1.2,
    // One width for both buttons, whatever their label (Lydia, 7 Oct 2026).
    width: { xs: '100%', sm: 280 },
    whiteSpace: 'nowrap',
    '&:hover': { backgroundColor: '#e55e00' },
};

const plainCardSx = {
    border: `1px solid ${BORDER}`,
    borderRadius: '14px',
    boxShadow: '0px 2px 8px rgba(0,0,0,0.04)',
    backgroundColor: 'var(--brand-surface, #ffffff)',
};

const cardHeaderIconSx = {
    width: 36, height: 36, borderRadius: '10px',
    backgroundColor: 'rgba(255, 105, 0, 0.08)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
};

const CardHeader = ({ icon: Icon, title }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pb: 2, mb: 2.5, borderBottom: `1px solid ${BORDER}` }}>
        <Box sx={cardHeaderIconSx}>
            <Icon sx={{ fontSize: 20, color: ORANGE }} />
        </Box>
        <Typography sx={{ fontSize: '17px', fontWeight: 800, color: TEXT }}>
            {title}
        </Typography>
    </Box>
);

const SolutionChildrenAssessmentOverviewPage = () => {
    const history = useHistory();
    const { authUser } = useAuth();
    /*
     * Two buttons under the cards (7 Oct 2026): Preview, and the screen where
     * the assessment is linked — My Schools for a partner or a network. A
     * school or a teacher has no My Schools; they link from My Classes.
     */
    /*
     * A school that bought for itself (direct plan) is a level-21 owner, which
     * resolveWorkspace reads as a partner — but its menu is the school's, with
     * My Classes and no My Schools (LA-SW-12Oct-01). isClientWorkspace is the
     * same test Main.comp.jsx picks that menu with.
     */
    const hasMySchools = !!authUser?.is_school_network
        || (resolveWorkspace(authUser) === PBDQ_WORKSPACE.PARTNER && !isClientWorkspace(authUser));
    const linkTarget = hasMySchools
        ? { label: 'Link Assessment to Schools', path: '/dq_pro/clients/list' }
        : { label: 'Link Assessment to Classes', path: '/dq_pro/classes' };
    // An LC Teacher links learning programs to classes, never assessments
    // (LA-TW-19Oct-02): Preview only.
    const canLinkAssessment = resolveWorkspace(authUser) !== PBDQ_WORKSPACE.TEACHER;

    return (
        <Box sx={{ width: '100%', maxWidth: '100%', px: { xs: 2, sm: 0 }, py: { xs: 3, sm: 5 } }}>
            <Typography sx={{ fontSize: { xs: '24px', sm: '30px' }, fontWeight: 800, color: TEXT, mb: 1.5 }}>
                Understand How Safe Students Are Online
            </Typography>
            <Typography sx={{ fontSize: '15px', color: MUTED, lineHeight: 1.6, mb: 5, }}>
                The COS Children Assessment shows how ready students are for life online — where they're doing
                well, where risks are emerging, and where additional support may be needed.
            </Typography>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3, mb: 4, alignItems: 'stretch' }}>
                {/* Card 1 — What it measures */}
                <Box sx={{ ...plainCardSx, p: { xs: 3, sm: 3.5 }, display: 'flex', flexDirection: 'column' }}>
                    <CardHeader icon={AssignmentOutlinedIcon} title="What it measures" />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {WHAT_IT_MEASURES.map((item) => (
                            <Box key={item.title}>
                                <Typography sx={{ fontSize: '14.5px', fontWeight: 700, color: TEXT, mb: 0.75 }}>
                                    {item.title}
                                </Typography>
                                <Typography sx={{ fontSize: '13.5px', color: MUTED, lineHeight: 1.7 }}>
                                    {item.desc}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Card 2 — Assessment at a glance */}
                <Box sx={{ ...plainCardSx, p: { xs: 3, sm: 3.5 }, display: 'flex', flexDirection: 'column' }}>
                    <CardHeader icon={BarChartOutlinedIcon} title="Assessment at a glance" />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.75 }}>
                        {AT_A_GLANCE.map((item) => (
                            <Box key={item.label}>
                                <Typography sx={{ fontSize: '12.5px', color: MUTED, mb: 0.5 }}>
                                    {item.label}
                                </Typography>
                                <Typography sx={{ fontSize: '20px', fontWeight: 800, color: ORANGE, lineHeight: 1.3 }}>
                                    {item.value}
                                </Typography>
                                {item.caption && (
                                    <Typography sx={{ fontSize: '12.5px', color: MUTED, mt: 0.25 }}>
                                        {item.caption}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                </Box>

                {/* Card 3 — What you get */}
                <Box sx={{ ...plainCardSx, p: { xs: 3, sm: 3.5 }, display: 'flex', flexDirection: 'column' }}>
                    <CardHeader icon={CheckCircleOutlineIcon} title="What you get" />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
                        {WHAT_YOU_GET.map((line) => (
                            <Box key={line} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                <Box sx={{
                                    width: 7, height: 7, borderRadius: '50%', backgroundColor: ORANGE,
                                    flexShrink: 0, mt: '7px',
                                }} />
                                <Typography sx={{ fontSize: '14px', color: MUTED, lineHeight: 1.7 }}>
                                    {line}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                {/* Both filled, as on the mockup (slide 19, LA-PW-07-Oct-01). */}
                <Button sx={primaryButtonSx} onClick={() => history.push('/dq_pro/solutions/children-assessment/preview')}>
                    Preview Assessment
                </Button>
                {canLinkAssessment && (
                    <Button sx={primaryButtonSx} onClick={() => history.push(linkTarget.path)}>
                        {linkTarget.label}
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default SolutionChildrenAssessmentOverviewPage;
